import { createContext, useContext, ReactNode, useEffect, useState, useMemo } from 'react';
import { useQuery, ApolloClient } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_RESOURCE_LIBRARY_NEWS_ITEMS_QUERY,
    getResourceLibraryNewsItemsQueryVariables,
} from '../queries/getResourceLibraryNewsItemsQuery';

import { ResourceLibraryNewsBaseItem } from '../types/resourceLibraryItems';
import { transformResourceLibraryNewsItemsResponse } from '../tools/transformResourceLibraryNewsItemsResponse';
import { GetResourceLibraryNewsItemListingQuery } from '../schemas/generated-types/graphql';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

interface ResourceLibraryNewsItemContextValue {
    items: ResourceLibraryNewsBaseItem[];
    loading: boolean;
    error: any;
    refetch: () => void; // refetches all Resource Library Items
    initialize: () => void;
}

const ResourceLibraryNewsItemContext = createContext<
    ResourceLibraryNewsItemContextValue | undefined
>(undefined);

interface ResourceLibraryNewsItemProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const ResourceLibraryNewsItemsProvider = ({
    children,
    apolloClient,
}: ResourceLibraryNewsItemProviderProps) => {
    const { locale } = useLocale();
    const [initialized, setInitialized] = useState(false);

    // load NewsItems
    const query = GET_RESOURCE_LIBRARY_NEWS_ITEMS_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery<GetResourceLibraryNewsItemListingQuery>(query, {
        variables: getResourceLibraryNewsItemsQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all', // Allow partial data instead of full rejection
        skip: !initialized, // Do not run query until initialization is triggered.
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized, queryError });

    const refetch = () => {
        if (initialized) {
            apolloClient.refetchQueries({ include: 'active' });
        } else {
            initialize();
        }
    };

    const initialize = () => {
        if (!initialized) {
            setInitialized(true);
        }
    };

    // choose data source: real query or fallback
    const items = useMemo(() => {
        const source = fallbackData ?? queryData;
        if (!source) {
            return [];
        }
        return transformResourceLibraryNewsItemsResponse(source);
    }, [queryData, fallbackData]);

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <ResourceLibraryNewsItemContext.Provider
            value={{
                items,
                loading,
                error,
                refetch,
                initialize,
            }}
        >
            {children}
        </ResourceLibraryNewsItemContext.Provider>
    );
};

// Unsafe hook: does not trigger initialization.
function useResourceLibraryNewsItemsUnsafe() {
    const context = useContext(ResourceLibraryNewsItemContext);
    if (!context) {
        throw new Error(
            'useResourceLibraryNewsItems must be used within a ResourceLibraryNewsItemsProvider'
        );
    }
    return context;
}

// Safe hook: triggers initialization for this provider.
function useResourceLibraryNewsItems() {
    const context = useResourceLibraryNewsItemsUnsafe();
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export {
    ResourceLibraryNewsItemsProvider,
    useResourceLibraryNewsItems,
    useResourceLibraryNewsItemsUnsafe,
};
export type { ResourceLibraryNewsItemContextValue };
